import {
  Badge,
  Button,
  ErrorBoundary,
  FlexBox,
  Icon,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import produce from 'immer';
import type {
  Controls,
  InstrumentPanel as InstrumentPanelT,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import { isResponseWithData } from 'lib-shared-auth';
import {
  CSSProperties,
  RefObject,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useUpdateEffect } from 'react-use';

import { analyticsApi } from 'apis/analytics';

import { AnalyticsControls } from '../../../../../controls/analyticsControls';
import { panelGridLayoutConfig } from '../../../../utils/panelGridLayoutConfig';
import { useAnalyticsState } from '../../../../utils/useAnalyticsState/useAnalyticsState';
import { usePanelMeasurements } from '../../utils/usePanelMeasurements';
import { UsePersistInstrumentSettingsResult } from '../../utils/usePersistInstrumentSettings';
import { InstrumentPanelTitle } from '../panel/components/title/instrumentPanelTitle';
import { InstrumentPanel } from '../panel/instrumentPanel';
import { InstrumentRendererError } from '../renderer/components/error/instrumentRendererError';
import { InstrumentRenderer } from '../renderer/instrumentRenderer';

import styles from './analyticsPanelControls.module.scss';

type AnalyticsPanelControlsModalProps = {
  /** The instrument panel. */
  panel: InstrumentPanelT;
  /** The ref to the panel DOM element, used to get its measurements. */
  panelRef: RefObject<HTMLDivElement>;
  /** Called when the modal apply button is clicked. */
  onApply?: (panel: InstrumentPanelT) => void;
  /**
   * Called when the modal apply button is clicked and the changes should be
   * applied to all panels.
   */
  onApplyAll?: (panel: InstrumentPanelT) => void;
  /** Called when the modal is closed. */
  onClose: () => void;
};

export const AnalyticsPanelControlsModal = ({
  panel: initialPanel,
  panelRef,
  onApply,
  onApplyAll,
  onClose,
}: AnalyticsPanelControlsModalProps): JSX.Element => {
  const [panel, setPanel] = useState(initialPanel);
  const { dashboard } = useAnalyticsState(panel);
  const { panelContentHeight, panelWidth } = usePanelMeasurements(panelRef);

  const [
    readInstrumentsQuery,
    { instruments, isFetching, isError, isUninitialized },
  ] = analyticsApi.useLazyReadInstrumentsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      instruments: data?.data.instruments ?? [],
    }),
  });

  const readInstruments: ReadInstrumentsQueryFn = useCallback(
    async (arg) => {
      const controls = panel.controls!;

      // Get the existing data from the cache if it's the first request.
      const preferCacheValue = isUninitialized;

      const response = await readInstrumentsQuery(
        {
          id: panel.id,
          ...controls,
          meta: { ...arg?.meta },
        },
        preferCacheValue,
      );

      if (isResponseWithData(response)) {
        return response.data;
      }
    },
    [panel, readInstrumentsQuery, isUninitialized],
  );

  // Re-fetches the instrument data when the panel controls change.
  useUpdateEffect(() => {
    readInstruments();
  }, [panel.controls]);

  /** Handles updating the panel's controls. */
  const handleChangeControls = useCallback(
    (updatedControls: Partial<Controls>) => {
      setPanel((prevPanel) =>
        produce(prevPanel, (draft) => {
          Object.assign(draft.controls!, updatedControls);
        }),
      );
    },
    [],
  );

  /**
   * Handles updating the panel's `settings` control when the instrument
   * settings are changed.
   */
  const handleSetInstrumentSetting: UsePersistInstrumentSettingsResult['setInstrumentSetting'] =
    useCallback(
      (key, value) => {
        handleChangeControls({
          settings: {
            ...panel.controls!.settings,
            [key]: value,
          },
        });
      },
      [handleChangeControls, panel.controls],
    );

  const panelStyle = useMemo(
    (): CSSProperties => ({
      height: panelContentHeight || 'initial',
      // Narrower panels (configured width less than half the grid) are displayed with the same width
      // as on the main grid, while wider panels are allowed to stretch to fill the modal width.
      maxWidth:
        (panel.layout.w < panelGridLayoutConfig.cols / 2 && panelWidth) ||
        'initial',
      alignSelf: 'center',
    }),
    [panel.layout.w, panelContentHeight, panelWidth],
  );

  return (
    <Modal className={styles.modal} isOpen onClose={onClose}>
      <ModalHead
        title={
          <FlexBox gap="200" data-analytics-panel={panel.id}>
            <Badge variant="primary" size="medium">
              <FlexBox gap="200" alignItems="center">
                {dashboard.icon && <Icon name={dashboard.icon} size={16} />}
                {dashboard.title}
              </FlexBox>
            </Badge>

            <InstrumentPanelTitle panel={panel} />
          </FlexBox>
        }
        onClose={onClose}
      >
        <Text.Paragraph variant="4" subdued>
          Pinned from the <b>{dashboard.title}</b> dashboard. Below you can
          customize the period, filters and other settings for this panel in the
          quick view.
        </Text.Paragraph>
      </ModalHead>

      <ModalBody className={styles.modalBody}>
        <AnalyticsControls
          panel={panel}
          initiallyCollapsedFilters
          onChangeControls={handleChangeControls}
        />

        <InstrumentPanel panel={panel} showHeader={false} style={panelStyle}>
          <ErrorBoundary fallback={<InstrumentRendererError />}>
            <InstrumentRenderer
              panel={panel}
              instruments={instruments}
              isFetching={isFetching}
              isError={isError}
              onReadInstruments={readInstruments}
              onSetInstrumentSetting={handleSetInstrumentSetting}
            />
          </ErrorBoundary>
        </InstrumentPanel>
      </ModalBody>

      <ModalFoot>
        <FlexBox alignItems="center" gap="300">
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>

          {onApplyAll && (
            <Tooltip
              content={
                <>
                  The changes will apply to <b>all panels</b> in the quick view.
                </>
              }
            >
              <Button variant="secondary" onClick={() => onApplyAll(panel)}>
                Apply to all
              </Button>
            </Tooltip>
          )}

          {onApply && (
            <Tooltip
              content={
                <>
                  The changes will apply <b>only to this panel.</b>
                </>
              }
            >
              <Button variant="primary" onClick={() => onApply(panel)}>
                Apply
              </Button>
            </Tooltip>
          )}
        </FlexBox>
      </ModalFoot>
    </Modal>
  );
};
